import React from 'react';
import styled from 'styled-components';
import { 
  Brain, 
  Zap, 
  Loader2, 
  Award, 
  XCircle, 
  Pause 
} from 'lucide-react';
import { theme } from '../styles/theme';

const StatusCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;
  margin-bottom: ${theme.spacing[6]};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.primary[600]});
    opacity: 0.8;
  }
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const StatusIcon = styled.div<{ $variant: 'ready' | 'starting' | 'running' | 'completed' | 'failed' | 'paused' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  flex-shrink: 0;
  
  ${({ $variant }) => {
    switch ($variant) {
      case 'ready':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[600]};
        `;
      case 'starting':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[600]};
        `;
      case 'running':
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[600]};
        `;
      case 'completed':
        return `
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[600]};
        `;
      case 'failed':
        return `
          background: ${theme.colors.error[100]};
          color: ${theme.colors.error[600]};
        `;
      case 'paused':
        return `
          background: ${theme.colors.backgroundSecondary};
          color: ${theme.colors.textMuted};
        `;
      default:
        return `
          background: ${theme.colors.primary[100]};
          color: ${theme.colors.primary[600]};
        `;
    }
  }}
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusTitle = styled.h3`
  margin: 0 0 ${theme.spacing[3]} 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
  font-family: ${theme.typography.fontFamily.display};
`;

const StatusDescription = styled.p`
  margin: 0 0 ${theme.spacing[3]} 0;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.normal};
`;

const StatusMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const StatusDuration = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const StatusSubtext = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  font-style: italic;
`;

const ProgressContainer = styled.div`
  margin-top: ${theme.spacing[4]};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${theme.colors.backgroundSecondary};
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: ${theme.spacing[2]};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]});
  border-radius: 3px;
  transition: width 0.3s ease;
  width: ${({ $progress }) => $progress}%;
`;

const ProgressText = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  text-align: right;
`;

const SpinAnimation = styled.div`
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
  
  animation: spin 2s linear infinite;
`;

type StatusType = 'ready' | 'starting' | 'running' | 'completed' | 'failed' | 'paused';

interface AnalysisStatusCardProps {
  status: StatusType;
  progress?: number;
  currentStep?: string;
  customMessage?: string;
}

const statusConfig = {
  ready: {
    icon: Brain,
    title: "Ready to Generate Tender Response",
    content: "Our AI system will generate or refine your tender responses using your knowledge base and successful tender patterns. This comprehensive process includes document processing, requirement extraction, response generation, and quality scoring.",
    duration: "Expected duration: ~10 minutes",
    subtext: "Perfect time to grab a coffee or tea ☕🍵"
  },
  starting: {
    icon: Zap,
    title: "Response Generation in Progress",
    content: "Our AI agents are working through multiple phases: document processing, requirement extraction, response generation, and quality optimization. Each step builds upon the previous to ensure the highest quality output.",
    duration: "Estimated completion: ~10 minutes",
    subtext: "Feel free to take a short break — we've got this."
  },
  running: {
    icon: Loader2,
    title: "Response Generation in Progress",
    content: "Our AI agents are working through multiple phases: document processing, requirement extraction, response generation, and quality optimization. Each step builds upon the previous to ensure the highest quality output.",
    duration: null,
    subtext: "Great progress! Generation is proceeding as expected"
  },
  completed: {
    icon: Award,
    title: "Response Generation Complete!",
    content: "Your tender responses are ready! We've successfully processed your document, generated or refined responses using proven patterns, and created detailed recommendations to maximize your submission quality.",
    duration: null,
    subtext: "Review your optimized tender responses in the tabs above"
  },
  failed: {
    icon: XCircle,
    title: "Generation Encountered an Issue",
    content: "We encountered an unexpected issue during response generation. This could be due to document format, connectivity, or processing constraints. Your data remains secure and no changes were made.",
    duration: null,
    subtext: "Please try starting a new response or contact support if the issue persists"
  },
  paused: {
    icon: Pause,
    title: "Generation Temporarily Paused",
    content: "The response generation has been paused due to system maintenance or connectivity issues. Your progress has been saved and will resume automatically when the system is available.",
    duration: null,
    subtext: "Your work is safe - we'll pick up where we left off"
  }
};

const AnalysisStatusCard: React.FC<AnalysisStatusCardProps> = ({ 
  status, 
  progress, 
  currentStep, 
  customMessage 
}) => {
  const config = statusConfig[status];
  const IconComponent = config.icon;
  const showProgress = status === 'running' && typeof progress === 'number';

  return (
    <StatusCard>
      <StatusHeader>
        <StatusIcon $variant={status}>
          {status === 'running' ? (
            <SpinAnimation>
              <IconComponent size={24} />
            </SpinAnimation>
          ) : (
            <IconComponent size={24} />
          )}
        </StatusIcon>
        <StatusContent>
          <StatusTitle>{config.title}</StatusTitle>
          <StatusDescription>
            {customMessage || config.content}
          </StatusDescription>
          <StatusMeta>
            {config.duration && (
              <StatusDuration>{config.duration}</StatusDuration>
            )}
            <StatusSubtext>{config.subtext}</StatusSubtext>
          </StatusMeta>
        </StatusContent>
      </StatusHeader>
      
      {showProgress && (
        <ProgressContainer>
          <ProgressBar>
            <ProgressFill $progress={progress || 0} />
          </ProgressBar>
          <ProgressText>
            {progress}% complete
            {currentStep && ` • ${currentStep}`}
          </ProgressText>
        </ProgressContainer>
      )}
    </StatusCard>
  );
};

export default AnalysisStatusCard;